import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CorporateGroupFormModal from "./CorporateGroupFormModal";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import AppSelect from "../../components/Core/AppSelect/AppSelect";
import { FormCard } from "../../components/Core/FormCard";
import Button from "../../components/Core/Button/Button";
import AddButton from "../../components/Core/AddButton";
import Customer, {
	DeliveryMethod,
	InvoiceDetails,
	UpdateCustomerRequest,
} from "../../models/Customer";
import CorporateGroup from "../../models/CorporateGroup";
import { useTranslation } from "react-i18next";
import { useCorporateGroup, useCorporateGroups } from "../../networking/corporateGroup.hooks";
import { useUpdateCustomer } from "../../networking/customer.hooks";
import { useAssignableInvoiceTemplates } from "../../networking/invoice-template.hooks";
import FloatingButtonPanel from "../../components/Shared/FloatingButtonPanel/FloatingButtonPanel";
import useUnsavedChangesInFormChecker from "../../hooks/useUnsavedChangesChecker";
import CustomerTypeSelector, {
	CustomerProfileType,
	getCustomerProfileType,
} from "./CustomerTypeSelector";
import { toast } from "../../utils/toast";
import { useCustomFieldDefinitions, useOrganisation } from "../../networking/organisation.hooks";
import { useRefreshFortnoxCustomerDeliveryStateForCustomer } from "../../networking/invoice.hooks";
import { RefreshCw } from "lucide-react";
import {
	DeliveryMethodRadioGroup,
	getDeliveryErrorTarget,
	getDeliveryMethodOptions,
	getEffectiveDeliveryMethod,
	getGLNError,
	getMissingDeliveryField,
	isEInvoice,
	usesDeliveryField,
} from "../../components/Shared/DeliveryMethod";
import {
	CustomFieldFormValues,
	CustomFieldsSection,
	buildCustomFieldsPayloadWithStored,
	customFieldFormValues,
	customFieldsSchema,
	filterCustomFieldsByPlacements,
} from "../../components/Shared/CustomFields";

const FOLLOW_CONTRACT = "";

type InvoiceTabForm = InvoiceDetails & { customFields?: CustomFieldFormValues };

/** Result of the live Fortnox check when an e-invoice method is picked. */
type IntentCheckStatus = "checking" | "notInFortnox" | "inactive";

const CustomerInvoiceTab: React.FC<{ customer: Customer }> = ({ customer }) => {
	const { t } = useTranslation();
	const { mutate: updateCustomer } = useUpdateCustomer();

	const [customerType, setCustomerType] = useState<CustomerProfileType>(
		getCustomerProfileType(customer)
	);
	const [corporateGroupId, setCorporateGroupId] = useState<string | undefined>(
		customer?.corporateGroup?.id
	);
	const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
	/** Optional override; empty = follow the contract's template. */
	const [invoiceTemplateId, setInvoiceTemplateId] = useState<string>(
		customer?.invoiceTemplate?.id ?? FOLLOW_CONTRACT
	);
	const { templates: invoiceTemplates } = useAssignableInvoiceTemplates();
	const invoiceTemplateOptions = [
		{ id: FOLLOW_CONTRACT, name: t("customer.invoiceTemplateFollowContract") },
		...invoiceTemplates.map((tpl) => ({ id: tpl.id, name: tpl.name })),
		...(customer?.invoiceTemplate?.id &&
		!invoiceTemplates.some((tpl) => tpl.id === customer.invoiceTemplate?.id)
			? [{ id: customer.invoiceTemplate.id, name: t("contract.archivedInvoiceTemplate") }]
			: []),
	];

	const inCorporateGroup = customerType === "CORPORATE_GROUP";
	const isPrivate = customerType === "PRIVATE";

	const { data: organisation } = useOrganisation();
	const exportType = organisation?.invoiceSettings?.exportType;

	// Fortnox owns the method (and GLN) once it has seen e-invoice active: locked here
	const deliveryManagedBy = customer?.invoiceDetails?.deliveryManagedBy;
	const savedDeliveryMethod = getEffectiveDeliveryMethod(customer?.invoiceDetails);
	// In a koncern the group owns the method, unless the member is managed
	const deliveryLocked = !!deliveryManagedBy || inCorporateGroup;
	const deliveryMethodOptions = getDeliveryMethodOptions({
		exportType,
		privatePerson: isPrivate,
		current: savedDeliveryMethod,
	});

	// Per-customer "Hämta e-fakturakoppling från Fortnox", complements the
	// org-level button under Inställningar → Fakturering
	const { mutateAsync: refreshDeliveryState, isLoading: isRefreshingDeliveryState } =
		useRefreshFortnoxCustomerDeliveryStateForCustomer(customer?.id);

	// Live check when an e-invoice method is picked under Fortnox: the pick is
	// an intent until Fortnox has an active e-invoice koppling for the customer
	const [intentStatus, setIntentStatus] = useState<IntentCheckStatus>();
	const intentCheckRunning = useRef(false);
	// Set while a refreshed customer is expected from the intent check: the
	// next reset keeps the user's unsaved edits ("dirty")...
	const keepDirtyOnReset = useRef(false);
	// ...and, when Fortnox turned out to manage the customer, takes its method
	// and GLN even if the user had just changed them
	const takeManagedDelivery = useRef(false);

	async function refreshFromFortnox() {
		const res = await refreshDeliveryState().catch(() => undefined);

		if (!res) return;

		if (!res.inFortnox) {
			toast.info(t("deliveryMethod.refreshCustomer.notInFortnox"));
			return;
		}

		const description = t(
			res.updated
				? "deliveryMethod.refreshCustomer.updated"
				: "deliveryMethod.refreshCustomer.unchanged"
		);

		if (res.managed) {
			toast.success(
				t("deliveryMethod.refreshCustomer.managed", {
					method: t(`deliveryMethod.label.${res.deliveryMethod}` as any),
				}),
				{ description }
			);
		} else {
			toast.info(t("deliveryMethod.refreshCustomer.unmanaged"), { description });
		}
	}

	const { corporateGroups } = useCorporateGroups(
		{ page: 1, pageSize: 1000, sort: "name" },
		inCorporateGroup
	);

	// Org-defined customer fields on this tab: placement "faktura" renders
	// inside the Fakturamottagare card, "faktura-ovrigt" in its own "Övriga
	// faktureringsuppgifter" card. The Profil placements are edited on the
	// Profil tab; both tabs send the full active map on save.
	const allCustomerFieldDefinitions = useCustomFieldDefinitions("customer");
	const customFieldDefinitions = useMemo(
		() =>
			filterCustomFieldsByPlacements(allCustomerFieldDefinitions, ["faktura", "faktura-ovrigt"]),
		[allCustomerFieldDefinitions]
	);
	// A koncern member (the picked koncern, saved or not) gets the koncern's
	// value for group-owned fields: read-only, never required, never sent.
	// For the saved koncern the backend already merged its values into
	// `customer.customFields`; a newly picked one is fetched.
	const corporateGroupMember = inCorporateGroup && !!corporateGroupId;
	const pickedOtherGroup =
		corporateGroupMember && corporateGroupId !== customer?.corporateGroup?.id;
	const { corporateGroup: pickedGroup } = useCorporateGroup(
		pickedOtherGroup ? corporateGroupId : undefined
	);
	const corporateGroupValues = pickedOtherGroup
		? pickedGroup
			? pickedGroup.customFields ?? {}
			: undefined
		: customer?.customFields ?? {};
	// Custom fields, plus the data the chosen delivery method needs (mirrors
	// org-service; skipped when the method is owned by the group or Fortnox).
	const validationSchema = useMemo(
		() =>
			yup.object().shape({
				customFields: customFieldsSchema(customFieldDefinitions, undefined, {
					corporateGroupMember,
				}),
				deliveryMethod: yup
					.string()
					.test(
						"vat-number",
						"deliveryMethod.missingVatNumber",
						(value) => deliveryLocked || value !== "E_INVOICE_ORG" || !!customer?.vatNumber?.trim()
					),
				// 13 digits when set, for every method; required for E_INVOICE_GLN
				GLNCode: yup
					.string()
					.nullable()
					.test("gln", "deliveryMethod.field.GLNCode", function (value) {
						if (deliveryLocked) return true;

						const message = getGLNError(this.parent.deliveryMethod, value);

						return message ? this.createError({ message }) : true;
					}),
				email: yup
					.string()
					.nullable()
					.test("email", "deliveryMethod.field.email", function (value) {
						return deliveryLocked || this.parent.deliveryMethod !== "EMAIL" || !!value?.trim();
					}),
			}),
		[customFieldDefinitions, deliveryLocked, customer?.vatNumber, corporateGroupMember]
	);

	const {
		register,
		handleSubmit,
		formState,
		reset,
		control,
		watch,
		setValue,
		setError,
		getValues,
	} = useForm<InvoiceTabForm>({
		defaultValues: {
			...customer?.invoiceDetails,
			deliveryMethod: savedDeliveryMethod,
			invoiceName: customer?.invoiceDetails?.invoiceName || customer?.name,
			customFields: customFieldFormValues(customFieldDefinitions, customer?.customFields),
		},
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		// Refresh the (resolved) invoice details and customer type, e.g. after the
		// corporate group was edited in the modal
		const deliveryMethod = getEffectiveDeliveryMethod(customer?.invoiceDetails);

		reset(
			{
				...customer?.invoiceDetails,
				deliveryMethod,
				invoiceName: customer?.invoiceDetails?.invoiceName || customer?.name,
				customFields: customFieldFormValues(customFieldDefinitions, customer?.customFields),
			},
			// After the intent check the user's other unsaved edits survive
			{ keepDirtyValues: keepDirtyOnReset.current }
		);

		if (takeManagedDelivery.current && customer?.invoiceDetails?.deliveryManagedBy) {
			// Fortnox owns these two now; shouldDirty re-compares them with the
			// new defaults so they are no longer dirty
			setValue("deliveryMethod", deliveryMethod, { shouldDirty: true });
			setValue("GLNCode", customer.invoiceDetails.GLNCode, { shouldDirty: true });
			takeManagedDelivery.current = false;
		}

		keepDirtyOnReset.current = false;
		setCustomerType(getCustomerProfileType(customer));
		setCorporateGroupId(customer?.corporateGroup?.id);
		setInvoiceTemplateId(customer?.invoiceTemplate?.id ?? FOLLOW_CONTRACT);
	}, [customer, reset, setValue, customFieldDefinitions]);

	// A different customer: drop the status of the previous one's check
	useEffect(() => {
		setIntentStatus(undefined);
	}, [customer?.id]);

	// Live preview of the group-owned fields: picking another koncern in the
	// dropdown shows that group's name, address, email, GLN and delivery
	// method in the locked fields right away; going back to the saved group
	// (or to Företag) restores the customer's loaded values. Only the
	// koncern choice drives this, so a reset that keeps dirty edits is left alone.
	const savedGroupId = customer?.corporateGroup?.id;
	const appliedGroupPreview = useRef<string>();

	useEffect(() => {
		const previewId =
			inCorporateGroup && corporateGroupId && corporateGroupId !== savedGroupId
				? corporateGroupId
				: undefined;
		const previewGroup = previewId
			? corporateGroups?.corporateGroups.find((group) => group.id === previewId)
			: undefined;

		// Picked a group the list has not delivered yet (e.g. just created): wait
		if (previewId && !previewGroup) return;

		const key = previewGroup ? `group:${previewGroup.id}` : "customer";

		if (appliedGroupPreview.current === key) return;

		appliedGroupPreview.current = key;

		const stored = customer?.invoiceDetails;
		const values: Pick<
			InvoiceDetails,
			| "invoiceName"
			| "address1"
			| "address2"
			| "postalCode"
			| "city"
			| "email"
			| "GLNCode"
			| "deliveryMethod"
		> = previewGroup
			? {
					invoiceName: previewGroup.name,
					address1: previewGroup.invoiceDetails?.address1,
					address2: previewGroup.invoiceDetails?.address2,
					postalCode: previewGroup.invoiceDetails?.postalCode,
					city: previewGroup.invoiceDetails?.city,
					email: previewGroup.invoiceDetails?.email,
					// A Fortnox-managed member keeps its own method and GLN
					GLNCode: deliveryManagedBy ? stored?.GLNCode : previewGroup.invoiceDetails?.GLNCode,
					deliveryMethod: deliveryManagedBy
						? savedDeliveryMethod
						: getEffectiveDeliveryMethod(previewGroup.invoiceDetails),
			  }
			: {
					invoiceName: stored?.invoiceName || customer?.name,
					address1: stored?.address1,
					address2: stored?.address2,
					postalCode: stored?.postalCode,
					city: stored?.city,
					email: stored?.email,
					GLNCode: stored?.GLNCode,
					deliveryMethod: savedDeliveryMethod,
			  };

		for (const [field, value] of Object.entries(values))
			setValue(field as keyof typeof values, value, { shouldDirty: true });
	}, [
		inCorporateGroup,
		corporateGroupId,
		savedGroupId,
		corporateGroups,
		customer,
		deliveryManagedBy,
		savedDeliveryMethod,
		setValue,
	]);

	useUnsavedChangesInFormChecker(formState);

	const deliveryMethod = watch("deliveryMethod") ?? savedDeliveryMethod;
	const invoiceEmail = watch("email");

	// Switching to private person drops e-invoice: fall back to email or post
	useEffect(() => {
		if (deliveryLocked || deliveryMethodOptions.includes(deliveryMethod)) return;

		setValue("deliveryMethod", invoiceEmail?.trim() ? "EMAIL" : "PRINT", { shouldDirty: true });
	}, [deliveryLocked, deliveryMethodOptions, deliveryMethod, invoiceEmail, setValue]);

	/**
	 * Picking an e-invoice method for a saved, unmanaged Fortnox customer asks
	 * Fortnox whether the koppling is already active. If it is, Fortnox takes
	 * over (the refreshed customer arrives locked); otherwise the pick stays
	 * an intent and the status line says what happens until it is activated.
	 */
	async function checkEInvoiceIntent() {
		if (intentCheckRunning.current || isRefreshingDeliveryState) return;

		intentCheckRunning.current = true;
		keepDirtyOnReset.current = true;
		setIntentStatus("checking");

		const res = await refreshDeliveryState().catch(() => undefined);

		intentCheckRunning.current = false;

		// No refetch follows: later resets behave as usual again
		if (!res?.updated) keepDirtyOnReset.current = false;

		// The hook has toasted the error
		if (!res) {
			setIntentStatus(undefined);
			return;
		}

		if (res.managed) {
			// Picked while unmanaged, so a managed answer always changed the customer
			takeManagedDelivery.current = !!res.updated;
			setIntentStatus(undefined);
			toast.success(
				t("deliveryMethod.refreshCustomer.managed", {
					method: t(`deliveryMethod.label.${res.deliveryMethod}` as any),
				})
			);
			return;
		}

		// The user moved on to email/print while the check ran
		if (!isEInvoice(getValues("deliveryMethod"))) {
			setIntentStatus(undefined);
			return;
		}

		setIntentStatus(res.inFortnox ? "inactive" : "notInFortnox");
	}

	function onDeliveryMethodChange(value: DeliveryMethod) {
		setValue("deliveryMethod", value, { shouldDirty: true, shouldValidate: true });

		if (!isEInvoice(value)) {
			setIntentStatus(undefined);
			return;
		}

		if (exportType === "FORTNOX" && customer?.id && !deliveryManagedBy) {
			checkEInvoiceIntent();
		}
	}

	const onSubmit = handleSubmit(async (e) => {
		if (!customer?.id) return;

		if (inCorporateGroup && !corporateGroupId) {
			toast.error(t("corporateGroup.selectRequired"));
			return;
		}

		// Leaving a corporate group is signalled with corporateGroup: null,
		// which makes the backend materialize the group's invoice details
		// onto the customer
		const corporateGroup = inCorporateGroup
			? { id: corporateGroupId as string }
			: customer?.corporateGroup
			? null
			: undefined;

		const update: UpdateCustomerRequest = {
			privatePerson: customerType === "PRIVATE",
			corporateGroup,
			// null removes the override so the contract's template applies again
			invoiceTemplate: invoiceTemplateId ? { id: invoiceTemplateId } : null,
			// Every active customer key: this tab's from the form, the Profil tab's
			// from the stored customer (so a save here does not clear them).
			// Archived keys are kept by the backend.
			customFields: buildCustomFieldsPayloadWithStored(
				e.customFields,
				customFieldDefinitions,
				allCustomerFieldDefinitions,
				customer?.customFields,
				{ corporateGroupMember: inCorporateGroup }
			),
		};

		if (inCorporateGroup) {
			// Group-owned invoice details are managed on the corporate group;
			// only the customer's own reference is editable here
			update.invoiceDetails = { reference: e.reference };
		} else {
			// Explicit fields only: deliveryManagedBy/deliveryObservedAt are owned
			// by the integration and never echoed back. A managed customer sends its
			// stored method and GLN unchanged (the backend rejects changes to them).
			const updateCustomerInvoice: InvoiceDetails = {
				invoiceName: e.invoiceName,
				address1: e.address1,
				address2: e.address2,
				postalCode: e.postalCode,
				city: e.city,
				reference: e.reference,
				GLNCode: deliveryManagedBy ? customer.invoiceDetails?.GLNCode : e.GLNCode,
				email: e.email,
				deliveryMethod: deliveryManagedBy ? savedDeliveryMethod : e.deliveryMethod,
			};
			if (!updateCustomerInvoice.email) delete updateCustomerInvoice.email;
			update.invoiceDetails = updateCustomerInvoice;
		}

		// The hook toasts success/error and refetches the customer, which flows
		// back down as a new `customer` prop and resets the form.
		updateCustomer(
			{ customerId: customer.id, update },
			{
				// The hook toasts the error; also mark the field org-service named
				onError: ({ error }) => {
					const target = getDeliveryErrorTarget(error);

					if (target) setError(target.field, { message: target.message });
				},
			}
		);
	});

	return (
		<div>
			<form onSubmit={onSubmit}>
				<div className="max-w-[1200px] pr-4 py-6 xl:max-w-[2100px]">
					<div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
						<div className="flex flex-col gap-5">
							<FormCard title="INVOICE_RECIPIENT">
								<div className="flex flex-col gap-4">
									<div>
										<p className="mb-1.5 text-sm font-medium text-muted-foreground">
											{t("CUSTOMER_TYPE")}
										</p>
										<CustomerTypeSelector value={customerType} onChange={setCustomerType} />

										{inCorporateGroup && (
											<div className="mt-4 flex items-center gap-3 mb-1.5">
												<div className="flex-1">
													<AppSelect<CorporateGroup>
														name="corporateGroup"
														options={corporateGroups?.corporateGroups || []}
														getOptionLabel={(group) => group.name}
														getOptionValue={(group) => group.id}
														value={corporateGroupId}
														onChange={(id) => setCorporateGroupId(id || undefined)}
														placeholder="corporateGroup.selectPlaceholder"
														margin="0"
													/>
												</div>
												<AddButton
													label={t("corporateGroup.createNew")}
													onClick={() => setShowCreateGroupModal(true)}
												/>
											</div>
										)}
									</div>

									<AppInputNew
										{...register("invoiceName")}
										label="customer.invoiceName"
										placeholder=""
										disabled={inCorporateGroup}
									/>
									<AppInputNew
										{...register("reference")}
										label={inCorporateGroup ? "corporateGroup.referenceLabel" : "REFERENCE"}
										placeholder="REFERENCE_PLACEHOLDER"
									/>
								</div>
								{/* Customer fields placed under Fakturainformation — hidden when there are none. */}
								<CustomFieldsSection
									entity="customer"
									placement="faktura"
									variant="inline"
									definitions={allCustomerFieldDefinitions}
									control={control}
									register={register}
									errors={formState.errors}
									corporateGroupMember={corporateGroupMember}
									corporateGroupValues={corporateGroupValues}
								/>
							</FormCard>

							<FormCard title="customer.invoiceTemplate">
								<div className="flex flex-col gap-4">
									<AppSelect<{ id: string; name: string }>
										name="invoiceTemplate"
										label="customer.invoiceTemplateOverride"
										options={invoiceTemplateOptions}
										getOptionLabel={(option) => option.name}
										getOptionValue={(option) => option.id}
										value={invoiceTemplateId}
										onChange={(id) => setInvoiceTemplateId(id || FOLLOW_CONTRACT)}
										margin="0"
									/>
									<p className="text-xs text-muted-foreground">
										{t("customer.invoiceTemplateDesc")}
									</p>
								</div>
							</FormCard>
						</div>

						<div className="flex flex-col gap-5">
							<FormCard
								// The address is group-owned and read-only for a koncern member
								title={
									inCorporateGroup
										? `${t("INVOICE_ADDRESS")} (${t("deliveryMethod.fromCorporateGroup")})`
										: t("INVOICE_ADDRESS")
								}
								skipTranslation
							>
								<div className="flex flex-col gap-4">
									<AppInputNew
										{...register("address1")}
										placeholder="INVOICE_ADDRESS_LINE_1_PLACEHOLDER"
										disabled={inCorporateGroup}
									/>
									<AppInputNew
										{...register("address2")}
										placeholder="INVOICE_ADDRESS_LINE_2"
										disabled={inCorporateGroup}
									/>
									<div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
										<AppInputNew
											{...register("postalCode")}
											placeholder="POSTAL_CODE_PLACEHOLDER"
											disabled={inCorporateGroup}
										/>
										<AppInputNew
											{...register("city")}
											placeholder="CITY_PLACEHOLDER"
											disabled={inCorporateGroup}
										/>
									</div>
								</div>
							</FormCard>

							<FormCard
								title="E_INVOICE"
								action={
									exportType === "FORTNOX" &&
									customer?.id && (
										<button
											type="button"
											className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
											disabled={isRefreshingDeliveryState || formState.isDirty}
											title={
												formState.isDirty
													? t("deliveryMethod.refreshCustomer.saveFirst")
													: undefined
											}
											onClick={refreshFromFortnox}
										>
											<RefreshCw
												className={`h-3.5 w-3.5 ${isRefreshingDeliveryState ? "animate-spin" : ""}`}
											/>
											{t("deliveryMethod.refreshCustomer.button")}
										</button>
									)
								}
							>
								<div className="flex flex-col gap-4">
									<div className="mb-2 flex flex-col gap-4">
										<DeliveryMethodRadioGroup
											value={deliveryMethod}
											onChange={onDeliveryMethodChange}
											options={
												// A locked member shows its (group's or Fortnox's) value even if
												// it falls outside what this org could pick
												deliveryLocked && !deliveryMethodOptions.includes(deliveryMethod)
													? [deliveryMethod, ...deliveryMethodOptions]
													: deliveryMethodOptions
											}
											exportType={exportType}
											managedBy={deliveryManagedBy}
											observedAt={customer?.invoiceDetails?.deliveryObservedAt}
											disabled={deliveryLocked}
											note={
												inCorporateGroup && !deliveryManagedBy
													? t("deliveryMethod.fromCorporateGroup")
													: !deliveryLocked &&
													  deliveryMethod === "E_INVOICE_ORG" &&
													  !formState.errors.deliveryMethod &&
													  getMissingDeliveryField("E_INVOICE_ORG", {
															vatNumber: customer?.vatNumber,
													  })
													? t("deliveryMethod.missingVatNumber")
													: exportType === "FORTNOX" &&
													  !deliveryLocked &&
													  !intentStatus &&
													  isEInvoice(deliveryMethod)
													? t("deliveryMethod.eInvoiceIntent")
													: undefined
											}
											errors={
												formState.errors.deliveryMethod?.message &&
												t(formState.errors.deliveryMethod.message as any)
											}
										/>
										{intentStatus && !deliveryLocked && (
											<p
												className={`-mt-2 text-xs ${
													intentStatus === "checking" ? "text-gray-500" : "text-amber-700"
												}`}
											>
												{t(`deliveryMethod.intentCheck.${intentStatus}` as any)}
											</p>
										)}
									</div>
									<AppInputNew
										{...register("email")}
										label="EMAIL_INVOICE"
										placeholder="EMAIL_INVOICE_PLACEHOLDER"
										// Outside Fortnox only the field the chosen method uses is editable; the
										// other keeps its value. Under Fortnox email is always editable and GLN
										// until Fortnox manages the customer.
										disabled={
											inCorporateGroup ||
											!usesDeliveryField(deliveryMethod, "email", {
												exportType,
												managed: !!deliveryManagedBy,
											})
										}
										mandatory={!deliveryLocked && deliveryMethod === "EMAIL"}
										errors={formState.errors.email?.message}
									/>
									<AppInputNew
										{...register("GLNCode")}
										label="customer.gln"
										placeholder="GLN_PLACEHOLDER"
										disabled={
											inCorporateGroup ||
											!!deliveryManagedBy ||
											!usesDeliveryField(deliveryMethod, "GLNCode", {
												exportType,
												managed: !!deliveryManagedBy,
											})
										}
										mandatory={!deliveryLocked && deliveryMethod === "E_INVOICE_GLN"}
										errors={formState.errors.GLNCode?.message}
									/>
								</div>
							</FormCard>

							{/* Customer fields placed under "Övriga faktureringsuppgifter" — own card, hidden when there are none. */}
							<CustomFieldsSection
								entity="customer"
								placement="faktura-ovrigt"
								title="Övriga faktureringsuppgifter"
								definitions={allCustomerFieldDefinitions}
								control={control}
								register={register}
								errors={formState.errors}
								corporateGroupMember={corporateGroupMember}
								corporateGroupValues={corporateGroupValues}
							/>
						</div>
					</div>
				</div>
				<FloatingButtonPanel>
					<Button width="9.75rem">{t("SAVE_CHANGES")}</Button>
				</FloatingButtonPanel>
			</form>

			{showCreateGroupModal && (
				<CorporateGroupFormModal
					onClose={() => setShowCreateGroupModal(false)}
					onSaved={(group) => setCorporateGroupId(group.id)}
				/>
			)}
		</div>
	);
};

export default CustomerInvoiceTab;
